/**
 * Press logo bar.
 *
 * <press-logo-bar> - row of press logos where one is active at a time. The
 * active logo is slid to the center of the viewport, shown at full opacity,
 * and its quote is shown above the row. Advances on a timer (data-interval,
 * seconds) that pauses while hovered, keyboard-focused or off screen; logos can also
 * be clicked, or swiped on touch screens.
 */
(function () {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SWIPE_THRESHOLD = 40;

  if (customElements.get('press-logo-bar')) return;

  class PressLogoBar extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-viewport]');
      this.track = this.querySelector('[data-track]');
      this.logos = Array.from(this.querySelectorAll('[data-logo]'));
      this.quotes = Array.from(this.querySelectorAll('[data-quote]'));
      if (!this.track || this.logos.length < 2) return;

      this.index = 0;
      this.interval = (parseFloat(this.dataset.interval) || 5) * 1000;
      this.hovered = false;
      this.focused = false;
      this.inView = false;
      this.editorPaused = false;

      this.logos.forEach((logo, index) => {
        logo.addEventListener('click', () => {
          this.goTo(index);
          this.restart();
        });
      });

      this.addEventListener('mouseenter', () => this.setPaused('hovered', true));
      this.addEventListener('mouseleave', () => this.setPaused('hovered', false));
      // Only keyboard focus pauses - a tapped logo keeps focus on touch screens,
      // which would otherwise stop autoplay for good.
      this.addEventListener('focusin', (event) => {
        if (event.target.matches(':focus-visible')) this.setPaused('focused', true);
      });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.setPaused('focused', false);
      });

      this.viewport.addEventListener('touchstart', (event) => {
        this.touchStartX = event.touches[0].clientX;
        this.touchStartY = event.touches[0].clientY;
      }, { passive: true });
      this.viewport.addEventListener('touchend', (event) => this.onTouchEnd(event), { passive: true });

      this.sectionObserver = new IntersectionObserver(([entry]) => {
        this.setPaused('inView', entry.isIntersecting);
      });
      this.sectionObserver.observe(this);

      // The centering offset depends on rendered logo widths, so recompute it
      // whenever the viewport resizes or a lazy logo image finishes loading.
      this.resizeObserver = new ResizeObserver(() => this.position());
      this.resizeObserver.observe(this.viewport);
      this.track.querySelectorAll('img').forEach((image) => {
        if (!image.complete) image.addEventListener('load', () => this.position(), { once: true });
      });

      this._onMotionChange = () => this.restart();
      reducedMotion.addEventListener('change', this._onMotionChange);

      if (window.Shopify && window.Shopify.designMode) {
        this._onBlockSelect = (event) => {
          const index = this.logos.findIndex((logo) => logo.dataset.blockId === event.detail.blockId);
          if (index === -1) return;
          this.editorPaused = true;
          this.goTo(index);
          this.restart();
        };
        this._onBlockDeselect = () => {
          this.editorPaused = false;
          this.restart();
        };
        document.addEventListener('shopify:block:select', this._onBlockSelect);
        document.addEventListener('shopify:block:deselect', this._onBlockDeselect);
      }

      this.position();
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      if (this.sectionObserver) this.sectionObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this._onMotionChange) reducedMotion.removeEventListener('change', this._onMotionChange);
      if (this._onBlockSelect) document.removeEventListener('shopify:block:select', this._onBlockSelect);
      if (this._onBlockDeselect) document.removeEventListener('shopify:block:deselect', this._onBlockDeselect);
    }

    get canAutoplay() {
      return this.inView && !this.hovered && !this.focused && !this.editorPaused && !reducedMotion.matches;
    }

    setPaused(key, value) {
      this[key] = value;
      this.restart();
    }

    restart() {
      window.clearTimeout(this.timer);
      if (!this.canAutoplay) return;
      this.timer = window.setTimeout(() => {
        this.goTo((this.index + 1) % this.logos.length);
        this.restart();
      }, this.interval);
    }

    goTo(index) {
      const count = this.logos.length;
      this.index = ((index % count) + count) % count;

      this.logos.forEach((logo, i) => {
        const active = i === this.index;
        logo.classList.toggle('is-active', active);
        if (active) {
          logo.setAttribute('aria-current', 'true');
        } else {
          logo.removeAttribute('aria-current');
        }
      });

      this.quotes.forEach((quote, i) => {
        const active = i === this.index;
        quote.classList.toggle('is-active', active);
        if (active) {
          quote.removeAttribute('aria-hidden');
        } else {
          quote.setAttribute('aria-hidden', 'true');
        }
      });

      this.style.setProperty('--press-logo-bar-progress', this.index / (count - 1));
      this.position();
    }

    // The track is padded by 50% of the viewport, so moving it left by the
    // active logo's distance from the padding edge plus half its width
    // centers that logo.
    position() {
      const item = this.logos[this.index].parentElement;
      const paddingLeft = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
      const offset = item.offsetLeft - paddingLeft + item.offsetWidth / 2;
      this.track.style.setProperty('--press-logo-bar-offset', `${-offset}px`);
    }

    onTouchEnd(event) {
      if (this.touchStartX == null) return;
      const deltaX = event.changedTouches[0].clientX - this.touchStartX;
      const deltaY = event.changedTouches[0].clientY - this.touchStartY;
      this.touchStartX = null;

      if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) < Math.abs(deltaY)) return;
      this.goTo(this.index + (deltaX < 0 ? 1 : -1));
      this.restart();
    }
  }

  customElements.define('press-logo-bar', PressLogoBar);
})();
